/**
 * The <e2-nested-tabs> component is used to show Nested Tabs in two different layouts
 *
 * Case 1. Reviews slot is not present:
 * +----------------------------+---------------------------------------------------------+
 * |      Component#1 (1/3)     |                    Component#4 (2/3)                    |
 * +----------------------------+                         Reviews                         |
 * |      Component#2 (1/3)     |                                                         |
 * +----------------------------+                                                         |
 * |      Component#3 (1/3)     |                                                         |
 * +----------------------------+                                                         |
 * |      Component#5 (1/3)     |                                                         |
 * +----------------------------+---------------------------------------------------------+
 *
 * Case 2. Reviews slot is present:
 * +------------------------------------------+-------------------------------------------+
 * |             Component#1 (1/2)            |             Component#2 (1/2)             |
 * +------------------------------------------+-------------------------------------------+
 * |             Component#3 (1/2)            |             Component#5 (1/2)             |
 * +------------------------------------------+-------------------------------------------+
 *
 * HTML Structure
 * <!-- Example -->
 * <e2-nested-tabs>
 *     <div v-slot="brand-info"><!-- Brand Information CONTENT HERE --></div>
 *     <div v-slot="product-info"><!-- Product Information CONTENT HERE --></div>
 *     <div v-slot="medical-info"><!-- Medical Information CONTENT HERE --></div>
 *     <div v-slot="label-info"><!-- Label Information CONTENT HERE --></div>
 *     <div v-slot="order-and-delivery-info"><!-- Order and Delivery Information CONTENT HERE --></div>
 *     <div v-slot="reviews"><!-- Reviews CONTENT HERE --></div>
 * </e2-nested-tabs>
 */

import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';

class NestedTabs extends LightLitElement {
    static get properties() {
        return {
            contentFix: { type: Boolean, attribute: false },
            withReviews: {
                type: Boolean,
            },
        };
    }

    constructor() {
        super();
        this.withReviews = false;
    }

    wrapContent(content) {
        return html`
            <div class="col-12 col-lg-6">${content}</div>
        `;
    }

    slotProductInfo() {
        return html`
            <v-slot name="product-info"></v-slot>
        `;
    }

    slotDeliveryInfo() {
        return html`
            <v-slot name="order-and-delivery-info"></v-slot>
        `;
    }

    slotLabelInfo() {
        return html`
            <v-slot name="label-info"></v-slot>
        `;
    }

    slotMedicalInfo() {
        return html`
            <v-slot name="medical-info"></v-slot>
        `;
    }

    onReviewsChange() {
        const content = this.querySelector('v-slot[name="reviews"]').assignedNodes()[0];
        if (content) {
            this.withReviews = true;
        }
    }

    noReviewsTemplate() {
        return html`
            <div class="col-12">
                <v-slot name="brand-info"></v-slot>
            </div>
            ${this.productInfo && !this.labelInfo && !this.medicalInfo && this.orderAndDeliveryInfo
                ? [
                      this.wrapContent(this.slotProductInfo()),
                      this.wrapContent(this.slotDeliveryInfo()),
                  ]
                : ''}
            ${this.productInfo && this.labelInfo && !this.medicalInfo && this.orderAndDeliveryInfo
                ? [
                      this.wrapContent([this.slotProductInfo(), this.slotDeliveryInfo()]),
                      this.wrapContent(this.slotLabelInfo()),
                  ]
                : ''}
            ${this.productInfo && !this.labelInfo && this.medicalInfo && this.orderAndDeliveryInfo
                ? [
                      this.wrapContent([this.slotProductInfo(), this.slotDeliveryInfo()]),
                      this.wrapContent(this.slotMedicalInfo()),
                  ]
                : ''}
            ${this.productInfo && this.labelInfo && this.medicalInfo && this.orderAndDeliveryInfo
                ? [
                      this.wrapContent([this.slotProductInfo(), this.slotLabelInfo()]),
                      this.wrapContent([this.slotMedicalInfo(), this.slotDeliveryInfo()]),
                  ]
                : ''}
        `;
    }

    withReviewsTemplate() {
        return html`
            <div class="col-12 col-lg-4">
                <v-slot name="brand-info"></v-slot>
                <v-slot name="product-info"></v-slot>
                <v-slot name="medical-info"></v-slot>
                <v-slot name="label-info"></v-slot>
                <v-slot name="order-and-delivery-info"></v-slot>
            </div>
        `;
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        setTimeout(this.onComponentReady.bind(this), 0);
    }

    onComponentReady = () => {
        this.productInfo = this.querySelector("[v-slot='product-info']");
        this.medicalInfo = this.querySelector("[v-slot='medical-info']");
        this.labelInfo = this.querySelector("[v-slot='label-info']");
        this.orderAndDeliveryInfo = this.querySelector("[v-slot='order-and-delivery-info']");
        this.reviews = this.querySelector("[v-slot='reviews']");
        this.contentFix = true;
    };

    render() {
        return html`
            <div class="row">
                ${this.contentFix
                    ? this.withReviews
                        ? this.withReviewsTemplate()
                        : this.noReviewsTemplate()
                    : ''}
                <div
                    class="col-12 col-lg-8 m-top-medium--desktop ${this.withReviews
                        ? ''
                        : 'd-none'}"
                >
                    <v-slot name="reviews" @slotchange=${this.onReviewsChange}></v-slot>
                </div>
            </div>
        `;
    }
}

export default () => customElements.define('e2-nested-tabs', NestedTabs);
